import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCompany } from "@/contexts/CompanyContext";
import { Building2, Trash2, Loader2, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface ManageCompaniesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageCompaniesDialog({
  open,
  onOpenChange,
}: ManageCompaniesDialogProps) {
  const { selectedCompanyId, setSelectedCompanyId } = useCompany();
  const [companyToDelete, setCompanyToDelete] = useState<number | null>(null);

  const { data: companies = [], refetch } = trpc.company.list.useQuery();

  const deleteMutation = trpc.company.delete.useMutation({
    onSuccess: () => {
      toast.success("Company deleted successfully");
      refetch();
      if (companyToDelete === selectedCompanyId) {
        // If deleted current company, switch to another or clear selection
        const remaining = companies.filter(c => c.id !== companyToDelete);
        if (remaining.length > 0) {
          setSelectedCompanyId(remaining[0].id);
        } else {
          // No companies left - ideally handle this safely
          // setSelectedCompanyId(undefined); // Depending on context type
        }
      }
      setCompanyToDelete(null);
    },
    onError: error => {
      toast.error(`Failed to delete company: ${error.message}`);
      setCompanyToDelete(null);
    },
  });

  const handleDeleteClick = (id: number) => {
    setCompanyToDelete(id);
  };

  const confirmDelete = () => {
    if (companyToDelete) {
      deleteMutation.mutate({ id: companyToDelete });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Manage Companies</DialogTitle>
            <DialogDescription>
              View and manage your registered companies.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {companies.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No companies found.
              </div>
            ) : (
              <div className="space-y-2">
                {companies.map(company => (
                  <div
                    key={company.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      company.id === selectedCompanyId
                        ? "bg-accent/50 border-accent"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="font-medium truncate">
                          {company.name}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">
                          {company.legalForm} • {company.uid || "No UID"}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                      onClick={() => handleDeleteClick(company.id)}
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending &&
                      companyToDelete === company.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!companyToDelete}
        onOpenChange={open => !open && setCompanyToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Company?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this company? This action cannot
              be undone. All associated data (transactions, documents, accounts,
              reports) will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground transition-colors"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Company"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
